import { isTrainingExpenseDisplayRow } from "./trainingOtherExpenseSync";

export function descriptionTaskOwnerId(entry) {
  if (!entry || typeof entry !== "object") return "";
  const o = entry.taskownerid;
  if (o && typeof o === "object" && o._id != null) return String(o._id).trim();
  return String(o ?? "").trim();
}

/** Trim + collapse whitespace + lowercase — stable key for grouping/consolidation. */
export function normalizeOtherExpenseDescriptionKey(raw) {
  return String(raw ?? "")
    .trim()
    .replace(/\ufeff/g, "")
    .replace(/[\s\u00a0\u2000-\u200b]+/g, " ")
    .trim()
    .toLowerCase();
}

/** Display label with leading/trailing spaces removed (keeps original casing). */
export function canonicalOtherExpenseDescriptionLabel(raw) {
  return String(raw ?? "")
    .trim()
    .replace(/\ufeff/g, "")
    .replace(/[\s\u00a0\u2000-\u200b]+/g, " ")
    .trim();
}

/** Group description-task rows by normalized description (avoids "Airfare" vs "Airfare "). */
export function groupDescriptionTasksByNormalizedDescription(rows = []) {
  const byKey = {};
  for (const d of rows) {
    const key = normalizeOtherExpenseDescriptionKey(d?.description);
    if (!key) continue;
    if (!byKey[key]) byKey[key] = [];
    byKey[key].push(d);
  }
  return byKey;
}

export function finalDescriptionMatchesNormalizedKey(row, descOrKey) {
  const target = normalizeOtherExpenseDescriptionKey(descOrKey);
  if (!target) return false;
  return normalizeOtherExpenseDescriptionKey(row?.description) === target;
}

export function descriptionTaskProjectId(entry) {
  if (!entry || typeof entry !== "object") return "";
  const p = entry.projectid ?? entry.projectId ?? entry.ProjectId ?? entry.project;
  if (p && typeof p === "object" && (p._id != null || p.id != null)) {
    return String(p._id ?? p.id ?? "").trim();
  }
  return String(p ?? "").trim();
}

/** ERP / lead / quotation-linked ids that may appear on description-task `projectid`. */
export function buildProjectDescriptionAliasIds(projectRow, projectsList = []) {
  const ids = new Set();
  const add = (v) => {
    const s = String(v ?? "").trim();
    if (s) ids.add(s);
  };
  if (!projectRow) return ids;
  add(projectRow._id);
  add(projectRow.ProjectID);
  add(projectRow.projectid);
  add(projectRow.projectId);
  const leadLike = projectRow.lead || projectRow.Lead;
  if (leadLike && typeof leadLike === "object") {
    add(leadLike._id);
    add(leadLike.id);
    add(leadLike.projectid);
  }
  add(projectRow.leadid);
  add(projectRow.leadId);
  const qid = String(
    projectRow.quotationId ?? projectRow.quotationID ?? projectRow.quotationid ?? ""
  ).trim();
  if (qid && Array.isArray(projectsList)) {
    const hit = projectsList.find(
      (p) => String(p?.quotationId ?? p?.quotationID ?? "").trim() === qid
    );
    add(hit?._id);
  }
  return ids;
}

export function descriptionMatchesProjectAliases(entry, aliasIds) {
  const pid = descriptionTaskProjectId(entry);
  return Boolean(pid && aliasIds.has(pid));
}

export function dedupeDescriptionTasksById(rows = []) {
  const byId = new Map();
  for (const r of rows) {
    const id = String(r?._id ?? "").trim();
    if (!id) continue;
    if (!byId.has(id)) byId.set(id, r);
  }
  return Array.from(byId.values());
}

/**
 * Merge `descriptionProjectTracking` rows with persisted `/descriptiontask` rows.
 * When tracking is training-only, ignore `oldtaskid` so manual lines from the previous basket are not dropped.
 */
export function mergeProjectAssignedDescriptionsForOp(trackingRows = [], taskRows = []) {
  const tracking = trackingRows.map((r) => ({ ...r }));
  const trainingOnlyTracking =
    tracking.length > 0 && tracking.every((r) => isTrainingExpenseDisplayRow(r));
  const supersededOldIds = trainingOnlyTracking
    ? new Set()
    : new Set(
        tracking.map((r) => String(r?.oldtaskid ?? "").trim()).filter(Boolean)
      );
  const trackingRowIds = new Set(
    tracking.map((r) => String(r?._id ?? "").trim()).filter(Boolean)
  );
  const merged = [...tracking];
  for (const task of taskRows) {
    const id = String(task?._id ?? "").trim();
    if (!id) continue;
    if (trackingRowIds.has(id)) continue;
    if (supersededOldIds.has(id)) continue;
    merged.push({ ...task });
  }
  return merged;
}

/** Full-project merge: tracking + persisted description tasks for any assignee on this project. */
export function mergeProjectDescriptionRowsForAssignUI({
  projectRow,
  trackingRows = [],
  persistedRows = [],
  legacyPersistedRows = [],
  projectsList = [],
}) {
  const aliasIds = buildProjectDescriptionAliasIds(projectRow, projectsList);
  const trackingProject = (trackingRows || []).filter((d) =>
    descriptionMatchesProjectAliases(d, aliasIds)
  );
  const persistedPool = dedupeDescriptionTasksById(
    ([]).concat(persistedRows || [], legacyPersistedRows || [])
  ).filter((d) => descriptionMatchesProjectAliases(d, aliasIds));
  return mergeProjectAssignedDescriptionsForOp(
    dedupeDescriptionTasksById(trackingProject),
    persistedPool
  );
}
